import {
  Center,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Select,
  Text,
} from "@chakra-ui/react";
import Pagination from "@material-ui/lab/Pagination";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { compact, every, filter, find, map } from "lodash";
import CategoryDrawer from "../src/components/categoryDrawer";
import { Error, FreeDeliveryOffer, TopBand } from "../src/components/common";
import NoProductsFound from "../src/components/products/noProductsFound";
import ProductCard from "../src/components/products/productCard";

import {
  ITEMS_PER_PAGE_ODD,
  PRODUCT_PARENT_CATEGORIES,
} from "../src/constants";
import { fetchProductCategories, fetchProducts } from "../src/redux/actions";
import { wrapper } from "../src/redux/store";

export default function Shop({ errorMessage }) {
  if (errorMessage) {
    return <Error errorMessage={errorMessage} />;
  }
  const { cart, products, productCategories } = useSelector((state) => state);
  const cartProductIds = cart.map((product) => product.id);

  const [page, setPage] = useState(1);
  const [filterDrawer, setFilterDrawer] = useState(false);
  const [allProducts, setAllProducts] = useState(products);
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [selectedCategories, setSelectedCategories] = useState(null);

  const closeFilterDrawer = () => setFilterDrawer(false);

  const onChangeFilters = (e) => {
    e.preventDefault();
    const category = find(
      productCategories,
      (pc) => pc.name === e.target.value
    );

    if (category) {
      const parentCategory = find(
        productCategories,
        (pc) => pc.id === category.parent
      );
      setSelectedCategories({
        ...selectedCategories,
        [parentCategory.slug]: {
          id: category.id,
          slug: category.slug,
          label: category.name,
        },
      });
    }
  };

  useEffect(() => {
    if (selectedCategories) {
      const selectedCategoryIds = compact(
        map(Object.values(selectedCategories), (sc) => sc.id)
      );

      const updatedFilteredProducts = filter(allProducts, (p) => {
        const productCategoryIds = map(p.categories, (c) => c.id);
        return every(selectedCategoryIds, (selectedCategoryId) =>
          productCategoryIds.includes(selectedCategoryId)
        );
      });

      setFilteredProducts(updatedFilteredProducts);
      setPage(1);
    } else {
      setFilteredProducts(allProducts);
      setPage(1);
    }
  }, [selectedCategories]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const handleClearFilters = () => setSelectedCategories(null);

  const getProductsList = () => {
    if (filteredProducts && filteredProducts.length === 0) {
      return (
        <Flex width="40%" margin="auto" my="5%">
          <NoProductsFound px={50} clearFilters={handleClearFilters} />
        </Flex>
      );
    }
    return (
      <Flex
        flexWrap="wrap"
        width={{ xl: "90%", "2xl": "1350px" }}
        justifyContent={{
          base: "center",
          md: "space-around",
          lg: "space-around",
          xl: "space-between",
          "2xl": "space-between",
        }}
      >
        {filteredProducts &&
          filteredProducts
            .slice((page - 1) * ITEMS_PER_PAGE_ODD, page * ITEMS_PER_PAGE_ODD)
            .map((product) => (
              <ProductCard
                key={product?.id}
                product={product}
                cartProductIds={cartProductIds}
              />
            ))}
      </Flex>
    );
  };

  return (
    <>
      <TopBand title="Our Products" />
      <Center mt="10" flexDirection="column">
        <Flex
          mb="5"
          alignItems="center"
          width={{ base: "90%", "2xl": "1350px" }}
        >
          {filteredProducts.length > 0 && (
            <Flex
              alignItems={{ base: "flex-start", sm: "center" }}
              justifyContent={{ base: "flex-start", sm: "center" }}
              flexDirection={{ base: "column", sm: "row" }}
            >
              <Flex alignItems={{ base: "center" }}>
                <Text color="titleColor" variant="productCardDescription">
                  {`Showing ${(page - 1) * ITEMS_PER_PAGE_ODD + 1}-${Math.min(
                    page * ITEMS_PER_PAGE_ODD,
                    filteredProducts.length
                  )} of ${filteredProducts.length} Results`}
                </Text>
              </Flex>
              <FreeDeliveryOffer />
            </Flex>
          )}
        </Flex>
        <Flex
          mb="5"
          flexWrap="wrap"
          alignItems={"center"}
          justifyContent={"flex-start"}
          width={{ base: "90%", "2xl": "1350px" }}
        >
          {map(Object.values(PRODUCT_PARENT_CATEGORIES), (parentCategory) => {
            const dropDownLabel =
              selectedCategories && selectedCategories[parentCategory.slug]
                ? selectedCategories[parentCategory.slug].label
                : "";

            return (
              <Select
                mb="3"
                mr="3"
                size="md"
                width={{ base: 150, lg: 200 }}
                borderColor={"maroon"}
                value={dropDownLabel}
                placeholder={parentCategory.label}
                onChange={(e) => onChangeFilters(e)}
              >
                {map(
                  filter(
                    productCategories,
                    (pc) => pc.parent === parentCategory.id
                  ),
                  (category) => (
                    <option key={category.id}>{category.name}</option>
                  )
                )}
              </Select>
            );
          })}
          {selectedCategories && (
            <Text
              mb="3"
              ml="10px"
              _hover={{ cursor: "pointer" }}
              variant="priceText"
              onClick={handleClearFilters}
            >
              Reset filters
            </Text>
          )}
        </Flex>
        {getProductsList()}
      </Center>
      {filteredProducts.length > 0 && (
        <Center my="10">
          <Pagination
            page={page}
            size="large"
            color="primary"
            variant="outlined"
            onChange={(event, val) => setPage(val)}
            count={
              products
                ? Math.ceil(filteredProducts.length / ITEMS_PER_PAGE_ODD)
                : 0
            }
          />
        </Center>
      )}
      <Drawer
        size="xs"
        placement="left"
        isOpen={filterDrawer}
        onClose={closeFilterDrawer}
      >
        <DrawerOverlay>
          <DrawerContent>
            <DrawerBody>
              <Text variant="drawerTitleText" my="5">
                Product Categories
              </Text>
              <CategoryDrawer
                closeFilterDrawer={closeFilterDrawer}
                productCategories={productCategories}
              />
            </DrawerBody>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>
    </>
  );
}

export const getStaticProps = wrapper.getStaticProps((store) => async () => {
  try {
    await store.dispatch(fetchProducts());
    await store.dispatch(fetchProductCategories());
  } catch (err) {
    return {
      props: {
        errorMessage: err.message,
      },
    };
  }
});
